import classNames from 'classnames';

import { ForecastDaySummary } from 'hooks/useGetForecast';
import { useSettingsContext } from 'providers/SettingsProvider';
import { Units, UnitsOfMeasurement } from 'utils/constants';
import DayOfTheWeek from 'components/common/dates/DayOfTheWeek';
import Astrological from '../astrological/Astrological';
import ForecastHourly from '../hourly/ForecastHourly';
import Integer from 'components/common/text/Integer';

type ForecastDayProps = {
  className?: string;
  forecast?: ForecastDaySummary;
};

function ForecastDay({ className, forecast }: ForecastDayProps) {
  const settings = useSettingsContext();
  const isMetric = UnitsOfMeasurement.Metric === settings.units;

  if (forecast) {
    return (
      <div data-testid="forecast-day" className={classNames('px-2', className)}>
        <DayOfTheWeek date={forecast.date} className="text-lg" relative />

        {/* Conditions this day */}
        <div className="mb-12">
          <div className="flex items-center">
            <img
              src={`https:${forecast.day.condition.icon}`}
              alt={forecast.day.condition.text}
              className="mr-2"
            />
            <div className="flex flex-col items-end text-sm">
              <div>
                High{' '}
                <Integer
                  value={isMetric ? forecast.day.maxtemp_c : forecast.day.maxtemp_f}
                  unit={isMetric ? Units.Celsius : Units.Fahrenheit}
                />
              </div>
              <div>
                Low{' '}
                <Integer
                  value={isMetric ? forecast.day.mintemp_c : forecast.day.mintemp_f}
                  unit={isMetric ? Units.Celsius : Units.Fahrenheit}
                />
              </div>
            </div>
          </div>
          <div className="text-lg">{forecast.day.condition.text}</div>
          <div>
            <span>
              <Integer value={forecast.day.daily_chance_of_rain} unit={Units.Percent} /> chance of
              precipitation
            </span>
            {forecast.day.totalprecip_in > 0 && (
              <span className="ml-1">
                totaling{' '}
                <Integer
                  value={isMetric ? forecast.day.totalprecip_mm : forecast.day.totalprecip_in}
                  unit={isMetric ? Units.Millimeter : Units.Inch}
                />
              </span>
            )}
          </div>
        </div>

        {/* Hourly forecast */}
        <ForecastHourly className="mb-12" hours={forecast.hour} />

        {/* Sunrise and Sunset details */}
        <Astrological className="mb-12" astrological={forecast.astro} date={forecast.date} />
      </div>
    );
  }

  return null;
}

export default ForecastDay;
